/**
 * ChildHoroscopePage
 * Детский гороскоп — страница для родителей (Группа 5.4)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { renderMarkdown } from '../../utils/renderMarkdown';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import ParentGuide from '../../components/ParentGuide';
import { setMetaTags, resetMetaTags } from '../../utils/seo';

// ============================================================
// Типы
// ============================================================

interface ChildHoroscopeData {
  personality: string;
  talents: string[];
  challenges: string[];
  parentingAdvice: string[];
  educationTips: string[];
  aiInterpretation: string;
}

interface ApiResponse {
  success: boolean;
  data?: ChildHoroscopeData;
  message?: string;
}

// ============================================================
// Стили
// ============================================================

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(13, 20, 40, 0.85)',
  cardBorder: 'rgba(212, 175, 55, 0.12)',
  cardShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  pink: '#E91E63',
  purple: '#9C27B0',
  pinkLight: 'rgba(233, 30, 99, 0.15)',
  purpleLight: 'rgba(156, 39, 176, 0.15)',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  success: '#4caf50',
  warning: '#ff9800',
};

const goldAccentLine = (
  <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
);

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '24px 16px',
    color: COLORS.text,
    minHeight: '100vh',
    position: 'relative',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    ...headingStyle,
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: COLORS.textMuted,
  },
  form: {
    background: COLORS.cardBg,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: '24px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: COLORS.cardShadow,
    position: 'relative',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    color: COLORS.textMuted,
    fontWeight: 500,
  },
  input: {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    background: 'rgba(13, 20, 40, 0.6)',
    color: COLORS.text,
    fontSize: '14px',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
    color: '#0A0814',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  result: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  card: {
    background: COLORS.cardBg,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: '24px',
    padding: '20px',
    boxShadow: COLORS.cardShadow,
    position: 'relative',
  },
  cardTitle: {
    ...headingStyle,
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  tag: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    background: COLORS.pinkLight,
    border: `1px solid rgba(233, 30, 99, 0.2)`,
    color: COLORS.text,
  },
  tagWarning: {
    background: 'rgba(255, 152, 0, 0.15)',
    border: `1px solid rgba(255, 152, 0, 0.2)`,
  },
  text: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.text,
  },
  aiText: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: COLORS.text,
  },
  error: {
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'rgba(255, 107, 107, 0.15)',
    border: `1px solid rgba(255, 107, 107, 0.3)`,
    color: '#ff6b6b',
    fontSize: '14px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: COLORS.gold,
    textDecoration: 'none',
    fontSize: '14px',
    marginBottom: '16px',
    cursor: 'pointer',
  },
  list: {
    listStyle: 'none' as const,
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  bullet: {
    flexShrink: 0,
    color: COLORS.pink,
    fontWeight: 700,
  },
};

// ============================================================
// Компонент
// ============================================================

const ChildHoroscopePage: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ChildHoroscopeData | null>(null);

  useEffect(() => {
    setMetaTags({
      title: 'Детский гороскоп — Astranum',
      description: 'Узнайте таланты, таланты и рекомендации для вашего ребёнка',
    });
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => {
      resetMetaTags();
      window.removeEventListener('resize', handler);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!birthDate || !birthPlace) {
      setError('Укажите дату рождения и место рождения ребёнка');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post<ApiResponse>('/api/child/horoscope', {
        birthDate,
        birthTime: birthTime || '12:00',
        birthPlace,
      });

      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.message || 'Произошла ошибка при расчёте');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setBirthDate('');
    setBirthTime('');
    setBirthPlace('');
  };

  return (
    <div style={{ ...styles.container, padding: isMobile ? '16px 12px' : '24px 16px' }}>
      {goldAccentLine}
      {/* Back link */}
      <div
        style={styles.backLink}
        onClick={() => navigate('/forecasts')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/forecasts')}
      >
        ← Назад к прогнозам
      </div>

      {/* Header */}
      <div style={{ ...styles.header, marginBottom: isMobile ? '24px' : '32px' }}>
        <h1 style={{ ...styles.title, fontSize: isMobile ? '22px' : '28px' }}>Детский гороскоп</h1>
        <p style={styles.subtitle}>
          Узнайте таланты, особенности личности и рекомендации для воспитания вашего ребёнка
        </p>
      </div>

      {/* Form */}
      {!result && (
        <form style={styles.form} onSubmit={handleSubmit}>
          {goldAccentLine}
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Дата рождения *</label>
              <input
                type="date"
                style={styles.input}
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Время рождения</label>
              <input
                type="time"
                style={styles.input}
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Место рождения *</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Город, страна"
                value={birthPlace}
                onChange={(e) => setBirthPlace(e.target.value)}
                required
              />
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button
            type="submit"
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading}
          >
            {loading ? 'Рассчитываю...' : 'Рассчитать гороскоп'}
          </button>
        </form>
      )}

      {/* Results */}
      {result && (
        <div style={styles.result}>
          {/* Back button */}
          <div
            style={styles.backLink}
            onClick={handleReset}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleReset()}
          >
            ← Рассчитать для другого ребёнка
          </div>

          {/* Personality */}
          <div style={styles.card}>
            {goldAccentLine}
            <div style={styles.cardTitle}>🌟 Личность ребёнка</div>
            <p style={styles.text}>{result.personality}</p>
          </div>

          {/* Talents */}
          <div style={styles.card}>
            {goldAccentLine}
            <div style={styles.cardTitle}>✨ Таланты</div>
            <div style={styles.tagList}>
              {result.talents.map((talent, i) => (
                <span key={i} style={styles.tag}>{talent}</span>
              ))}
            </div>
          </div>

          {/* Challenges */}
          <div style={styles.card}>
            {goldAccentLine}
            <div style={{ ...styles.cardTitle, color: COLORS.warning }}>⚠️ Вызовы</div>
            <div style={styles.tagList}>
              {result.challenges.map((challenge, i) => (
                <span key={i} style={{ ...styles.tag, ...styles.tagWarning }}>{challenge}</span>
              ))}
            </div>
          </div>

          {/* Parent Guide */}
          <ParentGuide
            advice={result.parentingAdvice}
            educationTips={result.educationTips}
          />

          {/* AI Interpretation */}
          <div style={styles.card}>
            {goldAccentLine}
            <div style={styles.cardTitle}>✨ Интерпретация</div>
            <div
              style={styles.aiText}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(renderMarkdown(result.aiInterpretation)),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildHoroscopePage;
